import html2canvas from "html2canvas";
import { PDFDocument } from "pdf-lib";
import {
  ReimbursementV2Request,
  ReimbursementV2Item,
} from "../services/reimbursement-v2-api";

interface UserInfo {
  firstName: string;
  middleName?: string;
  lastName: string;
  homeAddress?: string;
}

/**
 * Creates a first page PDF from the custom HTML template populated with reimbursement data
 */
export const createReimbursementFormFirstPageFromTemplate = async (
  request: ReimbursementV2Request,
  userInfo: UserInfo,
): Promise<PDFDocument> => {
  const container = document.createElement("div");
  container.style.position = "absolute";
  container.style.left = "-9999px";
  container.style.width = "1000px";
  container.style.backgroundColor = "#ffffff";

  const htmlContent = await loadAndPopulateTemplate(request, userInfo);
  container.innerHTML = htmlContent;
  document.body.appendChild(container);

  try {
    // Wait for content to render
    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
      allowTaint: true,
    });

    const pdfDoc = await PDFDocument.create();
    const imgData = canvas.toDataURL("image/png");
    const img = await pdfDoc.embedPng(imgData);

    const pageWidth = 841.89; // A4 landscape
    const pageHeight = 595.28;
    const imgWidth = pageWidth;
    const imgHeight = (canvas.height / canvas.width) * imgWidth;

    const page = pdfDoc.addPage([pageWidth, pageHeight]);
    page.drawImage(img, {
      x: 0,
      y: pageHeight - imgHeight,
      width: imgWidth,
      height: imgHeight,
    });

    return pdfDoc;
  } finally {
    document.body.removeChild(container);
  }
};

async function loadAndPopulateTemplate(
  request: ReimbursementV2Request,
  userInfo: UserInfo,
): Promise<string> {
  const templatePath =
    "/templates/reimbursementForm/ReimbursementFormTemplate.html";
  const response = await fetch(templatePath);
  const html = await response.text();

  // Load and convert logo to base64
  const logoBase64 = await loadImageAsBase64(
    "/templates/reimbursementForm/resources/excelfore-logo-dark.png",
  );

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");

  // Replace logo paths with base64 data
  const logoImages = doc.querySelectorAll(
    "img[src*='excelfore-logo-dark.png']",
  );
  logoImages.forEach((img) => {
    img.setAttribute("src", logoBase64);
  });

  // Format submission date
  const submissionDate = new Date(request.submissionDate);
  const formattedDate = submissionDate.toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  // Format employee name
  const employeeName = userInfo.middleName
    ? `${userInfo.lastName}, ${userInfo.firstName} ${userInfo.middleName}`
    : `${userInfo.lastName}, ${userInfo.firstName}`;

  // Populate header fields
  const submissionDateEl = doc.getElementById("submissionDate");
  if (submissionDateEl) submissionDateEl.textContent = formattedDate;

  const employeeNameEl = doc.getElementById("employeeName");
  if (employeeNameEl) employeeNameEl.textContent = employeeName;

  const employeeAddressEl = doc.getElementById("employeeAddress");
  if (employeeAddressEl)
    employeeAddressEl.textContent = userInfo.homeAddress || "";

  // Populate items table
  const itemsTableBody = doc.getElementById("itemsTableBody");
  if (itemsTableBody) {
    const itemsToAdd = request.items.slice(0, 20); // Support up to 20 items
    itemsToAdd.forEach((item) => {
      const row = createItemRow(item);
      itemsTableBody.appendChild(row);
    });

    // Add empty rows for visual consistency (reduced to 3 to fit more items on page)
    const emptyRowsCount = Math.max(0, 3 - itemsToAdd.length);
    for (let i = 0; i < emptyRowsCount; i++) {
      const emptyRow = createEmptyRow();
      itemsTableBody.appendChild(emptyRow);
    }
  }

  // Populate total amount
  const totalAmountEl = doc.getElementById("totalAmount");
  if (totalAmountEl) {
    const totalFormatted = request.totalAmount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    totalAmountEl.textContent = totalFormatted;
  }

  return doc.documentElement.outerHTML;
}

function createItemRow(item: ReimbursementV2Item): HTMLTableRowElement {
  const row = document.createElement("tr");

  const itemDate = new Date(item.itemDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const amountInYen = (
    item.pricePerUnit.valueOf() *
    item.quantity *
    item.forexRate.valueOf()
  ).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const pricePerUnit = item.pricePerUnit.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const forexRate = item.forexRate.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  row.innerHTML = `
    <td class="date-col">${escapeHtml(itemDate)}</td>
    <td class="description-col">${escapeHtml(item.description)}</td>
    <td class="purpose-col">${escapeHtml(item.purpose)}</td>
    <td class="quantity-col">${escapeHtml(item.quantity.toString())}</td>
    <td class="price-col">${escapeHtml(pricePerUnit)}</td>
    <td class="currency-col">${escapeHtml(item.currency)}</td>
    <td class="amount-col">${escapeHtml(amountInYen)}</td>
    <td class="rate-col">${escapeHtml(forexRate)}</td>
  `;

  return row;
}

function createEmptyRow(): HTMLTableRowElement {
  const row = document.createElement("tr");
  row.className = "empty-row";
  row.innerHTML = `
    <td class="date-col"></td>
    <td class="description-col"></td>
    <td class="purpose-col"></td>
    <td class="quantity-col"></td>
    <td class="price-col"></td>
    <td class="currency-col"></td>
    <td class="amount-col"></td>
    <td class="rate-col"></td>
  `;
  return row;
}

function escapeHtml(text: string): string {
  const map: { [key: string]: string } = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  };
  return text.replace(/[&<>"']/g, (m) => map[m]);
}

async function loadImageAsBase64(imagePath: string): Promise<string> {
  try {
    const response = await fetch(imagePath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error(`Failed to load image from ${imagePath}:`, error);
    return ""; // Return empty string if image fails to load
  }
}
